// ============================================
// COMPONENT - ProspectoPremiumAnuncio
// ============================================
// Alerta discreta + modal informativo de Premium para propietarios que venden y aún no pagan.
// No es molesto a propósito: aparece una vez al día como mucho, y si la persona dice "no" el backend espera
// 15, 30 y luego 45 días antes de volver a mostrarlo. Contacto por WhatsApp de ventas, sin cupón automático.

import { useEffect, useRef, useState } from 'react';
import { FaCrown, FaWhatsapp } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import { useProspectoPremium } from '@almacenes/core';
import { openExternalLink } from '@/lib/premium';

const WHATSAPP_VENTAS = '56920201644';

const BENEFICIOS = [
  'Carga automática de folios para tus boletas electrónicas',
  'Mi Negocio Pro: ve qué productos te dejan más ventas',
  'Categorías personalizadas',
  'Pagos mixtos',
  'Reportes sin límite (hoy tienes 5 consultas al día)',
];

function urlWhatsapp(rutAlmacen: string | null): string {
  const rut = rutAlmacen ? ` El RUT de mi almacén es ${rutAlmacen}.` : '';
  const texto = `Hola, vi el anuncio de Premium en Almacenes Digitales y quiero solicitar el cupón de 10% de descuento para el primer mes.${rut}`;
  return `https://wa.me/${WHATSAPP_VENTAS}?text=${encodeURIComponent(texto)}`;
}

export const ProspectoPremiumAnuncio = () => {
  const { mostrarAnuncio, rutAlmacen, procesando, descartar, marcarInteresado } = useProspectoPremium();
  const [modalAbierto, setModalAbierto] = useState(false);
  const botonContactarRef = useRef<HTMLButtonElement>(null);

  // Accesibilidad del modal: Escape lo cierra y el foco entra en la acción principal al abrirse.
  useEffect(() => {
    if (!modalAbierto) return;
    botonContactarRef.current?.focus();
    const alPresionarTecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModalAbierto(false);
    };
    document.addEventListener('keydown', alPresionarTecla);
    return () => document.removeEventListener('keydown', alPresionarTecla);
  }, [modalAbierto]);

  if (!mostrarAnuncio) return null;

  const handleContactar = () => {
    // Queda registrado como interesado (lead) y no se le vuelve a mostrar el anuncio hasta pasada la espera.
    // Si ya había una petición en curso (doble clic) no se hace nada, para no abrir WhatsApp dos veces.
    if (!marcarInteresado()) return;
    openExternalLink(urlWhatsapp(rutAlmacen));
    setModalAbierto(false);
  };

  const handleNoGracias = () => {
    if (!descartar()) return;
    setModalAbierto(false);
  };

  return (
    <>
      {/* Alerta discreta, abajo a la derecha */}
      {!modalAbierto && (
        <div className="fixed bottom-4 right-4 z-40 w-[calc(100%-2rem)] max-w-sm bg-white rounded-xl shadow-xl border border-purple-200 p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 shrink-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <FaCrown className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-800 text-sm">Tu negocio está creciendo</p>
              <p className="text-xs text-gray-600 mt-0.5">
                Conoce Premium: más herramientas para vender y controlar tu almacén.
              </p>
              <div className="flex items-center gap-3 mt-3">
                <button
                  onClick={() => setModalAbierto(true)}
                  className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-semibold hover:bg-purple-700 transition-colors"
                >
                  Ver más
                </button>
                <button
                  onClick={handleNoGracias}
                  disabled={procesando}
                  className="text-xs text-gray-500 hover:text-gray-700 underline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  No, gracias
                </button>
              </div>
            </div>
            <button
              onClick={handleNoGracias}
              disabled={procesando}
              aria-label="Cerrar anuncio"
              className="p-1 rounded hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MdClose className="text-gray-400" />
            </button>
          </div>
        </div>
      )}

      {/* Modal informativo */}
      {modalAbierto && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
          onClick={() => setModalAbierto(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="prospecto-premium-titulo"
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-white rounded-full flex items-center justify-center">
                  <FaCrown className="text-xl text-purple-500" />
                </div>
                <div>
                  <h2 id="prospecto-premium-titulo" className="text-xl font-bold text-white">Almacenes Digitales Premium</h2>
                  <p className="text-sm text-purple-100">Las funciones con la corona morada</p>
                </div>
              </div>
              <button
                onClick={() => setModalAbierto(false)}
                aria-label="Cerrar"
                className="p-2 rounded-lg hover:bg-white/20 transition-colors"
              >
                <MdClose className="text-2xl text-white" />
              </button>
            </div>

            <div className="px-6 py-6">
              <ul className="space-y-2 mb-6">
                {BENEFICIOS.map((beneficio) => (
                  <li key={beneficio} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{beneficio}</span>
                  </li>
                ))}
              </ul>

              <p className="text-sm text-gray-600 mb-5">
                Si te interesa, escríbenos y te contamos los detalles. Te podemos dar un cupón de descuento para tu primer mes.
              </p>

              <button
                ref={botonContactarRef}
                onClick={handleContactar}
                disabled={procesando}
                className="w-full py-3 bg-green-500 text-white rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaWhatsapp className="text-xl" />
                Hablar con ventas por WhatsApp
              </button>
              <button
                onClick={handleNoGracias}
                disabled={procesando}
                className="w-full mt-3 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                No, gracias
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
